/* ============================================================
   Talention — Jerarquía puesto a puesto (lógica pura)

   El "nivel jerárquico" (LEVELS) clasifica los puestos en categorías
   genéricas. Esto es otra cosa: la dependencia real puesto a puesto,
   quién reporta a quién, a través de `position.reportsToId`.

   Sin React ni estado global: todo son funciones puras sobre el array
   de puestos, para poder testearlas con `node --test`.
   ============================================================ */

import { TOLERANCIA_PUNTOS } from "./ministerio.js";

/* Índice id -> puesto. */
function indexById(positions) {
  return new Map(positions.map(p => [p.id, p]));
}

/* Superior directo de un puesto, o null si es raíz o si el superior ya no existe. */
function getSupervisor(positionId, positions) {
  const byId = indexById(positions);
  const pos = byId.get(positionId);
  if (!pos || pos.reportsToId == null) return null;
  return byId.get(pos.reportsToId) || null;
}

/* Subordinados directos, en el orden en que aparecen en el array. */
function getDirectReports(positionId, positions) {
  return positions.filter(p => p.reportsToId === positionId);
}

/* Todos los descendientes (directos e indirectos) de un puesto.
   Recorrido iterativo con conjunto de visitados: si los datos llegasen
   con un ciclo, termina igualmente en vez de desbordar la pila. */
function getDescendantIds(positionId, positions) {
  const childrenOf = new Map();
  for (const p of positions) {
    if (p.reportsToId == null) continue;
    if (!childrenOf.has(p.reportsToId)) childrenOf.set(p.reportsToId, []);
    childrenOf.get(p.reportsToId).push(p.id);
  }
  const found = new Set();
  const pending = [...(childrenOf.get(positionId) || [])];
  while (pending.length > 0) {
    const id = pending.pop();
    if (found.has(id)) continue;
    found.add(id);
    for (const childId of childrenOf.get(id) || []) pending.push(childId);
  }
  return found;
}

/* ¿Puede `positionId` pasar a depender de `candidateId` sin romper el árbol?
   Prohibido: depender de sí mismo o de uno de sus propios descendientes. */
function canReportTo(positionId, candidateId, positions) {
  if (candidateId == null) return true;            // pasar a ser raíz siempre vale
  if (candidateId === positionId) return false;
  const byId = indexById(positions);
  if (!byId.has(candidateId)) return false;
  return !getDescendantIds(positionId, positions).has(candidateId);
}

/* Candidatos válidos como superior de un puesto. */
function validSupervisors(positionId, positions) {
  return positions.filter(p => canReportTo(positionId, p.id, positions));
}

/* Árbol de puestos a partir de reportsToId.

   Un puesto cuyo superior no existe (por ejemplo, porque se borró) se trata
   como raíz. Si los datos contuviesen un ciclo, sus miembros no colgarían de
   ninguna raíz: se añaden al final como raíces para que nunca desaparezcan
   de la pantalla.

   Devuelve nodos { position, children, depth }. */
function buildTree(positions) {
  const nodes = new Map(positions.map(p => [p.id, { position: p, children: [], depth: 0 }]));
  const childIds = new Map();
  const rootIds = [];

  for (const p of positions) {
    const hasParent = p.reportsToId != null && p.reportsToId !== p.id && nodes.has(p.reportsToId);
    if (!hasParent) { rootIds.push(p.id); continue; }
    if (!childIds.has(p.reportsToId)) childIds.set(p.reportsToId, []);
    childIds.get(p.reportsToId).push(p.id);
  }

  // Las aristas se enganchan durante el recorrido: un nodo ya colocado no
  // vuelve a engancharse, así un ciclo se corta en vez de repetirse sin fin.
  const placed = new Set();
  const attach = (id, depth) => {
    const node = nodes.get(id);
    node.depth = depth;
    node.children = [];
    for (const childId of childIds.get(id) || []) {
      if (placed.has(childId)) continue;
      placed.add(childId);
      node.children.push(nodes.get(childId));
      attach(childId, depth + 1);
    }
  };

  const roots = [];
  const openRoot = (id) => {
    placed.add(id);
    roots.push(nodes.get(id));
    attach(id, 0);
  };
  rootIds.forEach(id => { if (!placed.has(id)) openRoot(id); });
  // Puestos atrapados en un ciclo: no cuelgan de ninguna raíz, los rescatamos.
  positions.forEach(p => { if (!placed.has(p.id)) openRoot(p.id); });

  return roots;
}

/* Aplana el árbol en el orden en que se pinta, conservando la profundidad. */
function flattenTree(roots) {
  const out = [];
  const walk = (node) => {
    out.push(node);
    node.children.forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

/* Subir un puesto en la jerarquía: pasa a depender del superior de su superior.
   Devuelve { ok: true, reportsToId } o { ok: false, reason }. */
function promoteTarget(positionId, positions) {
  const byId = indexById(positions);
  const pos = byId.get(positionId);
  if (!pos) return { ok: false, reason: "El puesto no existe" };
  if (pos.reportsToId == null) return { ok: false, reason: "Ya está en el nivel más alto" };
  const supervisor = byId.get(pos.reportsToId);
  if (!supervisor) return { ok: false, reason: "Ya está en el nivel más alto" };
  return { ok: true, reportsToId: supervisor.reportsToId ?? null };
}

/* Bajar un puesto en la jerarquía: pasa a depender del hermano anterior.
   Devuelve { ok: true, reportsToId } o { ok: false, reason }. */
function demoteTarget(positionId, positions) {
  const byId = indexById(positions);
  const pos = byId.get(positionId);
  if (!pos) return { ok: false, reason: "El puesto no existe" };
  const siblings = positions.filter(p => (p.reportsToId ?? null) === (pos.reportsToId ?? null));
  const idx = siblings.findIndex(p => p.id === positionId);
  if (idx <= 0) return { ok: false, reason: "No hay ningún puesto por encima en su mismo nivel" };
  return { ok: true, reportsToId: siblings[idx - 1].id };
}

/* Incoherencias entre la jerarquía y la puntuación SVPT.

   Regla: un puesto subordinado no debería puntuar más que su superior. Solo
   se compara cuando ambas valoraciones están finalizadas; con una valoración
   a medias la comparación no significa nada.

   Devuelve un objeto { [positionId]: issue } para poder consultarlo por id. */
function detectHierarchyIssues(positions) {
  const byId = indexById(positions);
  const issues = {};
  for (const pos of positions) {
    if (pos.reportsToId == null) continue;
    const supervisor = byId.get(pos.reportsToId);
    if (!supervisor) continue;
    if (pos.status !== "FINALIZADA" || supervisor.status !== "FINALIZADA") continue;
    if (pos.points <= supervisor.points + TOLERANCIA_PUNTOS) continue;
    issues[pos.id] = {
      reason: "SUBORDINADO_SUPERA_SUPERIOR",
      supervisorId: supervisor.id,
      points: pos.points,
      supervisorPoints: supervisor.points,
      message: `${Math.round(pos.points)} pts frente a los ${Math.round(supervisor.points)} pts de ${supervisor.name}, del que depende`,
    };
  }
  return issues;
}

/* Deshace los superiores pedidos que dejan un ciclo en el organigrama ya
   aplicado. `nodos` es una lista de {id, reportsToId} con TODOS los superiores
   pedidos ya asignados (se modifica in situ); `pedidos` son {id, fila,
   anterior}, con `anterior` el superior que tenía el puesto antes del cambio.
   En cada ciclo se descarta el pedido de la fila más tardía (el puesto vuelve
   a su `anterior`) y se repite hasta que no quede ninguno. Devuelve los ids
   descartados, en el orden en que se descartaron. */
function romperCiclos(nodos, pedidos) {
  const porId = new Map(nodos.map(n => [n.id, n]));
  const vigentes = new Map(pedidos.map(p => [p.id, p]));
  const descartados = [];

  const buscarCiclo = () => {
    for (const inicio of vigentes.keys()) {
      const camino = [];
      let id = inicio;
      while (id != null && porId.has(id) && !camino.includes(id)) {
        camino.push(id);
        id = porId.get(id).reportsToId;
      }
      if (id == null || !camino.includes(id)) continue;
      const ciclo = camino.slice(camino.indexOf(id));
      // Un ciclo sin ningún pedido es previo a la importación: se ignora y se
      // sigue con los demás puntos de partida.
      if (ciclo.some(c => vigentes.has(c))) return ciclo;
    }
    return null;
  };

  for (let ciclo = buscarCiclo(); ciclo; ciclo = buscarCiclo()) {
    const culpables = ciclo.filter(id => vigentes.has(id));
    const peor = culpables.reduce((a, b) => (vigentes.get(b).fila > vigentes.get(a).fila ? b : a));
    porId.get(peor).reportsToId = vigentes.get(peor).anterior;
    vigentes.delete(peor);
    descartados.push(peor);
  }
  return descartados;
}

export {
  romperCiclos,
  getSupervisor,
  getDirectReports,
  getDescendantIds,
  canReportTo,
  validSupervisors,
  buildTree,
  flattenTree,
  promoteTarget,
  demoteTarget,
  detectHierarchyIssues,
};
